import { speechFailureDetails } from "./snag-speech-errors";
import type { SpeechDetails } from "./snag-speech-telemetry";
import type { SpeechModelStatus } from "./snag-vocal-transcription";

type Status = SpeechModelStatus | "loading";
type Dependencies = {
  download: () => Promise<void>;
  getStatus: () => Promise<SpeechModelStatus>;
  isActive: () => boolean;
  isOnline: () => boolean;
  onEvent?: (event: string, details: SpeechDetails, warning?: boolean) => void;
};

// One controller for the lifetime of the app, independent of mounted editors.
export function createSpeechModelPreparation(deps: Dependencies) {
  let status: Status = "loading";
  let started = false;
  let checking = false;
  let downloading = false;
  let pauses = 0;
  let generation = 0;
  let retryAt = 0;
  let retryDelay = 60000;
  let attempt = 0;
  let waitingSince: number | undefined;
  let waitingReported = false;
  const report = (
    event: string,
    details: SpeechDetails = {},
    warning = false
  ) => {
    try {
      deps.onEvent?.(
        event,
        {
          ...details,
          attempt,
          isActive: deps.isActive(),
          networkOnline: deps.isOnline(),
          status,
        },
        warning
      );
    } catch {
      /* Diagnostics must not stop model preparation. */
    }
  };
  let timer: ReturnType<typeof setTimeout> | undefined;
  const listeners = new Set<() => void>();
  const publish = (value: Status) => {
    if (status === value) return;
    status = value;
    report("preparation-status");
    listeners.forEach((listener) => listener());
  };
  const schedule = () => {
    clearTimeout(timer);
    if (!started || pauses || !deps.isActive()) return;
    const delay = status === "missing" || status === "pending" ? 10000 : 60000;
    timer = setTimeout(() => {
      void refresh();
    }, delay);
  };
  async function refresh() {
    if (!started || checking || pauses || !deps.isActive()) return;
    clearTimeout(timer);
    checking = true;
    const current = generation;
    const startedAt = Date.now();
    let deadline: ReturnType<typeof setTimeout> | undefined;
    try {
      const next = await Promise.race([
        deps.getStatus(),
        new Promise<never>((_, reject) => {
          deadline = setTimeout(
            () =>
              reject(
                Object.assign(new Error("Speech capability check timed out"), {
                  code: "SPEECH_CAPABILITY_TIMEOUT",
                })
              ),
            25000
          );
        }),
      ]);
      if (!started || current !== generation || pauses || !deps.isActive())
        return;
      publish(next);
      if (next === "ready") {
        retryAt = 0;
        retryDelay = 60000;
      }
      if (next === "missing" || next === "pending") {
        waitingSince ??= Date.now();
        if (
          !waitingReported &&
          deps.isOnline() &&
          Date.now() - waitingSince >= 120000
        ) {
          waitingReported = true;
          report(
            "preparation-waiting",
            { downloading, durationMs: Date.now() - waitingSince, retryAt },
            true
          );
        }
      } else {
        waitingSince = undefined;
        waitingReported = false;
      }
      if (
        next === "missing" &&
        !downloading &&
        deps.isOnline() &&
        Date.now() >= retryAt
      ) {
        downloading = true;
        attempt += 1;
        const downloadStartedAt = Date.now();
        const delay = retryDelay;
        retryAt = Date.now() + delay;
        retryDelay = Math.min(delay * 2, 300000);
        publish("pending");
        report("download-requested", { retryAt });
        // Do not await installation: status checks must continue even if the
        // native promise waits for connectivity, approval, or a late callback.
        void Promise.resolve()
          .then(() => deps.download())
          .then(() => {
            report("download-request-finished", {
              durationMs: Date.now() - downloadStartedAt,
            });
          })
          .catch((cause: unknown) => {
            retryAt = Date.now() + delay;
            report(
              "download-failed",
              {
                ...speechFailureDetails(cause),
                durationMs: Date.now() - downloadStartedAt,
                retryAt,
              },
              status !== "ready"
            );
          })
          .finally(() => {
            downloading = false;
            // Only the next native status check may declare the model ready.
            if (started) void refresh();
          });
      }
    } catch (cause) {
      if (started && current === generation && !pauses && deps.isActive()) {
        publish("unavailable");
        report(
          "capability-check-failed",
          {
            ...speechFailureDetails(cause),
            durationMs: Date.now() - startedAt,
          },
          true
        );
      }
    } finally {
      clearTimeout(deadline);
      checking = false;
      schedule();
    }
  }
  return {
    getSnapshot: () => status,
    pause: () => {
      pauses += 1;
      generation += 1;
      clearTimeout(timer);
      report("preparation-paused");
      let released = false;
      return () => {
        if (released) return;
        released = true;
        pauses -= 1;
        if (!pauses) {
          report("preparation-resumed");
          void refresh();
        }
      };
    },
    refresh,
    start: () => {
      if (started) return;
      started = true;
      report("preparation-started");
      void refresh();
    },
    stop: () => {
      started = false;
      retryAt = 0;
      retryDelay = 60000;
      generation += 1;
      clearTimeout(timer);
    },
    subscribe: (listener: () => void) => {
      listeners.add(listener);
      return () => {
        listeners.delete(listener);
      };
    },
  };
}
